import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { MobileSheet } from '../../../components/ui/MobileSheet';
import { SPESE_VISTE, VISTA_LABEL, adminHref, type SpeseVista } from '../../../lib/adminRoutes';
import { EmptyState } from '../../../components/ui/EmptyState';
import { canReadAdmin, canWriteAdmin } from '../adminApi';
import { AdminHeader, StoricoNote, ViewTabs, useStorico, useVista, type ViewTab } from '../shared/AdminShell';
import { SpeseDaPagare } from './SpeseDaPagare';
import { SpeseFisse } from './SpeseFisse';
import { SpesePagate } from './SpesePagate';
import { SpeseMobile } from './SpeseMobile';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import '../fatture/fatture.css';

/**
 * Spese (`/admin/spese`): cosa dobbiamo pagare a fornitori e per le spese
 * fisse, e quando (proposta § 2.3, lotto 5).
 *
 * Viste (`?vista=`):
 *   Spese da pagare   (predefinita) fornitori, spese fisse e contanti, con
 *                     "Per data / Per fornitore" in `?per=`
 *   Spese fisse       le ricorrenti per categoria + auto pagate a rate
 *   Spese già pagate  lo storico, con ricerca e mese
 *
 * Tasse e stipendi qui non ci sono (SPEC R3): vivono in Fisco e Dipendenti, e
 * il sottotitolo lo dice. L'unica azione di creazione è "Nuova spesa" in
 * intestazione (sul telefono in fondo): i bottoni doppi dentro le liste sono
 * spariti.
 */
const TITOLI: Record<SpeseVista, string> = {
  dapagare: 'Fatture dei fornitori, spese fisse e contanti ancora da pagare',
  fisse: 'Affitto, utenze, abbonamenti e rate che paghiamo ogni mese',
  pagate: 'Lo storico dei pagamenti ai fornitori',
};
/** Le etichette stanno in lib/adminRoutes.ts (VISTA_LABEL): qui solo il tooltip. */
const TABS: ViewTab<SpeseVista>[] = SPESE_VISTE.map((k) => ({ key: k, label: VISTA_LABEL.spese[k], title: TITOLI[k] }));

/**
 * Sul telefono la pagina è SpeseMobile (la risposta in cima, righe al posto di
 * schede, i gesti nel pannello della riga); "Nuova spesa" resta in fondo, con
 * le stesse tre scelte di qui. Sul computer resta questa.
 */
export function SpesePage() {
  const isMobile = useIsMobile();
  return isMobile ? <SpeseMobile nuovaSpesa={<NuovaSpesaMobile />} /> : <SpeseDesktop />;
}

function SpeseDesktop() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<SpeseVista>(SPESE_VISTE);
  const storico = useStorico();
  // Lo storico vale solo dove c'è una lista di documenti: le spese fisse sono tutte attuali.
  const conStorico = vista !== 'fisse';
  const viewTabs = <ViewTabs tabs={TABS} active={vista} onChange={setVista} />;

  // Senza l'Amministrazione ogni chiamata darebbe 403: si dice subito, una volta sola.
  if (!canReadAdmin(user)) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Spese" />
        <EmptyState brand title="Le spese non sono per il tuo profilo" description="Per vederle serve l'accesso all'Amministrazione: chiedilo a chi gestisce gli utenti." />
      </div>
    );
  }

  return (
    <div className="page adm-page">
      <AdminHeader
        title="Spese"
        // Tutto dentro uno span `.mute`: sul telefono admin.css spegne la prosa
        // del sottotitolo, e i due link da soli resterebbero lì senza frase.
        sub={<span className="mute">
          Fornitori, spese fisse e contanti: cosa pagare e quando. Le tasse sono in{' '}
          <Link className="underline" to={adminHref('fisco')}>Fisco</Link>, gli stipendi in{' '}
          <Link className="underline" to={adminHref('dipendenti')}>Dipendenti</Link>.
        </span>}
        actions={conStorico ? storico.button : undefined}
        menu={conStorico ? [storico.menuItem] : []}
        primary={canWrite ? <NuovaSpesaButton /> : undefined}
      />

      {conStorico && storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      {/* Ritmo della pagina (rifinitura.md §1.5): frase e fascia della vista,
          poi le linguette, poi i filtri. Le linguette le mette la vista, perché
          frase e fascia cambiano con lei; sul telefono così linguette e tasto
          Filtri restano sulla stessa riga (mobile.md 4bis.4). */}
      {vista === 'dapagare' && <SpeseDaPagare canWrite={canWrite} showHistory={storico.on} onPagate={() => setVista('pagate')} tabs={viewTabs} />}
      {vista === 'fisse' && <SpeseFisse tabs={viewTabs} />}
      {vista === 'pagate' && <SpesePagate showHistory={storico.on} tabs={viewTabs} />}

      {/* Sul telefono l'azione principale sta in fondo (mobile.md §4) e le
          scelte salgono dal basso (§6). */}
      {canWrite && (
        <div className="m-actionbar m-only">
          <NuovaSpesaMobile />
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// "+ Nuova spesa": tre voci
// ════════════════════════════════════════════════════════════════════

type Tipo = 'newPayable' | 'newRecurring' | 'newCashEntry';
const SCELTE: Array<{ kind: Tipo; icon: IconName; title: string; sub: string; payload?: Record<string, unknown> }> = [
  { kind: 'newPayable', icon: 'receipt', title: 'Fattura di un fornitore', sub: 'Una fattura ricevuta che non è ancora su FattureInCloud' },
  { kind: 'newRecurring', icon: 'repeat', title: 'Spesa fissa', sub: 'Affitto, utenze, abbonamenti, rate' },
  { kind: 'newCashEntry', icon: 'banknote', title: 'Uscita in contanti', sub: 'Pulizie, manutenzioni, piccole spese', payload: { direction: 'OUT' } },
];

function NuovaSpesaButton() {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);
  return (
    <div className="relative">
      <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" /> Nuova spesa <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] rounded-[var(--r-card)] border border-line bg-white shadow-lg overflow-hidden p-2" style={{ minWidth: 340 }}>
            {SCELTE.map((s) => (
              <Choice key={s.kind} icon={s.icon} title={s.title} sub={s.sub} onClick={() => { setOpen(false); openModal(s.kind, s.payload); }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NuovaSpesaMobile() {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);
  return (
    <>
      <button className="btn btn-primary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} /> Nuova spesa
      </button>
      <MobileSheet open={open} onClose={() => setOpen(false)} title="Che spesa vuoi registrare?">
        {SCELTE.map((s) => (
          <Choice key={s.kind} icon={s.icon} title={s.title} sub={s.sub} onClick={() => { setOpen(false); openModal(s.kind, s.payload); }} />
        ))}
      </MobileSheet>
    </>
  );
}

function Choice({ icon, title, sub, onClick }: { icon: IconName; title: string; sub: string; onClick: () => void }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer rounded-[var(--r-ctl)] transition" role="button" tabIndex={0}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className="icon-tile sm"><Icon name={icon} size={16} /></div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="mute text-[12px]">{sub}</div>
      </div>
      <Icon name="arrowRight" size={12} className="mute" />
    </div>
  );
}
